/**
 * The estimate page's presentation atoms. Page-internal by design: they
 * encode this page's honesty conventions (a gap is a short statement with its
 * reason in an ⓘ, never a dash or a fabricated £0.00) and its object-page
 * rules (one container per tab, key-value grids that hide empty values, a ▸
 * for detail, chips only for real documents).
 */
import { useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import type {
  EstimateDeliveryAddress,
  EstimateExpiry,
} from "@shared-core/data/api-types";
import {
  addressLines,
  type MoneyRow,
} from "@shared-core/domain/estimate-presentation";
import {
  formatLongDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { Card, CardContent } from "../../app/components/primitives/card";
import { cn } from "../../app/components/primitives/utils";
import { InfoTip } from "../../app/components/patterns/InfoTip";

/** A stated absence. The reason is the point — never render a bare dash. */
export function Gap({ children }: { children: ReactNode }) {
  return (
    <span className="font-normal text-muted-foreground italic">{children}</span>
  );
}

/**
 * The tab's one container. Named, so it is a landmark a screen-reader user
 * (and a test) can find; its sections carry their own small headings.
 */
export function TabContainer({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Card role="region" aria-label={label} className="gap-0">
      <CardContent className="divide-y divide-border py-0">
        {children}
      </CardContent>
    </Card>
  );
}

/** One section of a tab's container: a small heading, then its body. */
export function Section({
  title,
  label,
  info,
  aside,
  children,
}: {
  title?: string;
  /** The region's name, when it differs from the visible title. */
  label?: string;
  info?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-label={label ?? title} className="space-y-3 py-5">
      {title && (
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold">
            {title}
            {info && (
              <InfoTip label={`About ${title}`} className="ml-1">
                {info}
              </InfoTip>
            )}
          </h2>
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export interface KeyValue {
  label: string;
  /** Null, undefined or "" hides the row, unless `gap` says what is missing. */
  value: ReactNode | null | undefined;
  /** Shown instead of hiding the row — for a gap that is itself a fact. */
  gap?: string;
  /** The ⓘ beside the value. */
  info?: ReactNode;
}

/**
 * A key-value grid. Empty values are hidden (rule 5); a value whose absence
 * is a fact the reader needs — a price never set, a figure not yet synced —
 * passes `gap` and shows that statement instead.
 */
export function KeyValues({
  rows,
  className,
}: {
  rows: KeyValue[];
  className?: string;
}) {
  const shown = rows.filter(
    (r) => (r.value != null && r.value !== "") || r.gap,
  );
  if (shown.length === 0) return null;
  return (
    <dl
      className={cn(
        "grid grid-cols-[minmax(8rem,max-content)_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-sm",
        className,
      )}
    >
      {shown.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 break-words font-medium text-foreground">
            {row.value != null && row.value !== "" ? (
              row.value
            ) : (
              <Gap>{row.gap}</Gap>
            )}
            {row.info && (
              <InfoTip label={`About ${row.label}`} className="ml-1">
                {row.info}
              </InfoTip>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A titled ▸ section that starts closed — one per build-up on Price. */
export function Disclosure({
  title,
  id,
  children,
}: {
  title: string;
  id: string;
  children: ReactNode;
}) {
  const [isOpen, setOpen] = useState(false);
  return (
    <div className="py-1">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        onClick={() => setOpen(!isOpen)}
        className="flex w-full items-center gap-2 rounded py-2 text-left text-sm font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronRight
          aria-hidden
          className={cn(
            "size-4 text-muted-foreground transition-transform",
            isOpen && "rotate-90",
          )}
        />
        {title}
      </button>
      {isOpen && (
        <div id={id} className="max-w-3xl pb-3 pl-6">
          {children}
        </div>
      )}
    </div>
  );
}

/** A coloured bar above the tab's container, for something that needs action. */
export function AttentionBar({
  tone = "error",
  children,
  action,
}: {
  tone?: "error" | "warning";
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm",
        tone === "error"
          ? "border-[color:var(--color-error)]"
          : "border-[color:var(--color-warning)]",
      )}
    >
      <span className="min-w-0">{children}</span>
      {action}
    </div>
  );
}

export function SubHeading({
  children,
  aside,
}: {
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <h3 className="text-sm font-semibold">{children}</h3>
      {aside}
    </div>
  );
}

const ROW_STYLES: Record<MoneyRow["kind"], string> = {
  line: "text-sm",
  subtotal: "text-sm font-medium border-t border-border pt-1.5 mt-1.5",
  uplift: "text-sm",
  total: "text-sm font-semibold border-t border-border pt-1.5 mt-1.5",
  aside: "text-sm",
};

/** A money build-up. Every row carries a real figure — see `MoneyRow`. */
export function MoneyLadder({ rows }: { rows: MoneyRow[] }) {
  return (
    <dl className="space-y-1">
      {rows.map((row) => (
        <div
          key={row.key}
          className={`flex justify-between gap-4 ${ROW_STYLES[row.kind]}`}
        >
          <dt className={row.kind === "line" ? "text-muted-foreground" : ""}>
            {row.label}
            {row.pct != null && (
              <span className="text-muted-foreground">
                {" "}
                ({row.pct.toFixed(1)}%)
              </span>
            )}
            {row.pct == null && row.note != null && (
              <span className="text-muted-foreground"> ({row.note})</span>
            )}
          </dt>
          <dd className="tabular-nums whitespace-nowrap">
            {formatQuotedPrice(row.pence)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * en-GB date with no time, or null when there is nothing to format.
 *
 * Reserved for fields the replica genuinely holds as a date, never a
 * datetime — `estimate_date`, `required_by`, a Tharstern-required-date
 * expiry, a related job's `ordered_at`/`invoiced_at` (all `::date`-cast in
 * the query). Inventing a time on one of these would state a precision the
 * source never had (audit finding 87).
 */
export function formatDateOnly(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const formatted = formatLongDateOrDash(value);
  return formatted === "-" ? null : formatted;
}

/**
 * When the estimate stops being acceptable, or null when nothing says.
 * Tharstern's required-date expiry is a date-only field — no time to show.
 */
export function formatValidUntil(
  expiry: EstimateExpiry | null | undefined,
): string | null {
  if (!expiry?.expires_at) return null;
  const when = formatDateOnly(expiry.expires_at);
  return `${when}${expiry.is_expired ? " · expired" : ""}`;
}

/** An address as lines, with its contact and comment; an empty one says so. */
export function AddressBlock({
  address,
}: {
  address: EstimateDeliveryAddress;
}) {
  const lines = addressLines(address);
  return (
    <div className="text-sm">
      {lines.length > 0 ? (
        lines.map((line) => <div key={line}>{line}</div>)
      ) : (
        <Gap>Address held but empty</Gap>
      )}
      {address.contact && (
        <div className="text-muted-foreground mt-1">
          Contact: {address.contact}
        </div>
      )}
      {address.comment && (
        <div className="text-muted-foreground mt-1">{address.comment}</div>
      )}
    </div>
  );
}
